import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import OfferService, { Offer, CreateOfferPayload, UpdateOfferPayload, PaymentPlan, PaymentMilestone, PaymentMilestoneType, PaymentMilestoneTrigger, PaymentPlanTemplate, MilestoneInfo } from '../services/OfferService';
import CatalogService, { CatalogItem, CatalogItemKind } from '../services/CatalogService';
import ProjectService, { Project } from '../../contacts/services/ProjectService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import ProjectModal from '../../contacts/components/ProjectModal';
import OfferLineScopeDrawer from './OfferLineScopeDrawer';
import { parseLocaleNumber, formatFixed } from '../../../utils/numberFormatting';
import OfferHeaderCard from './OfferHeaderCard';
import OfferFormSection from './OfferFormSection';
import OfferLineItemsTable, { LineItemForm, CatalogMeta } from './OfferLineItemsTable';
import PaymentPlanSection from './PaymentPlanSection';
import RecurringContractSection, { RecurringConfig } from './RecurringContractSection';

const DEFAULT_TAX_RATE = 19;
const DEFAULT_PAYMENT_DAYS = 14;
const PAYMENT_PLAN_MIN_AMOUNT = 5000; // Show payment plan section for offers >= 5000€

const generatePaymentTermsText = (days: number): string => {
  return `Zahlbar innerhalb von ${days} Tagen nach Rechnungserhalt.`;
};

const DEFAULT_TERMS = generatePaymentTermsText(DEFAULT_PAYMENT_DAYS);

export default function OfferEditorPage(): React.JSX.Element {
  const { offerId } = useParams<{ offerId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = !!offerId && offerId !== 'new';
  const projectIdParam = searchParams.get('project_id');
  const partnerIdParam = searchParams.get('partner_id') || searchParams.get('business_partner_id');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [partner, setPartner] = useState<BusinessPartner | null>(null);

  const [projectSearch, setProjectSearch] = useState('');
  const [projectOptions, setProjectOptions] = useState<Project[]>([]);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);

  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerOptions, setPartnerOptions] = useState<BusinessPartner[]>([]);
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const partnerDropdownRef = useRef<HTMLDivElement>(null);

  const [title, setTitle] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState<string>('');
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemForm[]>([]);

  // Payment Plan state
  const [paymentPlanEnabled, setPaymentPlanEnabled] = useState(false);
  const [paymentPlanTemplate, setPaymentPlanTemplate] = useState<string>('none');
  const [paymentMilestones, setPaymentMilestones] = useState<PaymentMilestone[]>([]);
  const [existingPaymentPlan, setExistingPaymentPlan] = useState<PaymentPlan | null>(null);

  // FE-BILL-R34-1: Payment Plan Template & Milestone Assignment state
  const [paymentPlanTemplates, setPaymentPlanTemplates] = useState<PaymentPlanTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [milestoneAssignmentsByLineId, setMilestoneAssignmentsByLineId] = useState<Record<number, number | null>>({});
  const [pendingMilestoneByIndex, setPendingMilestoneByIndex] = useState<Record<number, number | null>>({});
  const [milestonesFromAssignments, setMilestonesFromAssignments] = useState<MilestoneInfo[]>([]);
  const [draggedLineIndex, setDraggedLineIndex] = useState<number | null>(null);
  const [dragOverMilestoneId, setDragOverMilestoneId] = useState<number | null | 'unassigned'>(null);
  const [savingLineId, setSavingLineId] = useState<number | null>(null);

  // Scope Drawer state - for viewing/editing line item scope (subtasks)
  const [scopeDrawerLine, setScopeDrawerLine] = useState<{ id: number; name: string } | null>(null);

  // FE-BILL-HF-R34-3: Template Preview state
  const [previewTemplate, setPreviewTemplate] = useState<PaymentPlanTemplate | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  const todayISO = new Date().toISOString().split('T')[0];
  const [recurringConfig, setRecurringConfig] = useState<RecurringConfig>({
    enabled: false,
    intervalUnit: 'MONTH',
    intervalCount: 1,
    startDate: todayISO,
    minTermMonths: 12,
    noticePeriodDays: 30,
    amountMode: 'FROM_TOTAL',
    customAmountGross: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (isEdit && offerId) {
        const offerData = await OfferService.get(parseInt(offerId, 10));
        setOffer(offerData);
        setTitle(offerData.title || '');
        setValidUntil(offerData.valid_until || '');
        setPaymentTermsDays(offerData.payment_terms_days != null ? String(offerData.payment_terms_days) : '');
        setTerms(offerData.terms || DEFAULT_TERMS);
        setNotes(offerData.notes || '');

        // FE-BILL-HF-R38-4: Build milestone assignments directly from lines (declare at higher scope)
        const byLineIdFromLines: Record<number, number | null> = {};
        console.log('[DEBUG] RAW offerData.line_items[0]:', offerData.line_items?.[0]);
        console.log('[DEBUG] RAW line_items keys:', offerData.line_items?.[0] ? Object.keys(offerData.line_items[0]) : 'no items');
        offerData.line_items?.forEach((li) => {
          if (li.id) {
            byLineIdFromLines[li.id] = li.payment_milestone_id ?? null;
          }
        });
        console.log('[DEBUG] byLineIdFromLines (from offer detail):', byLineIdFromLines);

        if (offerData.line_items && offerData.line_items.length > 0) {
          const catalogItemIds = offerData.line_items
            .map((li) => li.catalog_item_id)
            .filter((id): id is number => id !== undefined && id !== null);

          const catalogItemsMap = new Map<number, CatalogItem>();
          await Promise.all(
            catalogItemIds.map(async (id) => {
              try {
                const catalogItem = await CatalogService.get(id);
                catalogItemsMap.set(id, catalogItem);
              } catch {
                // Catalog item may have been deleted, ignore
              }
            })
          );

          const existingItems = offerData.line_items.map((li) => {
            const catalogItem = li.catalog_item_id ? catalogItemsMap.get(li.catalog_item_id) : undefined;
            return {
              id: li.id,
              position: li.position,
              catalog_item_id: li.catalog_item_id,
              name: li.name,
              description: li.description || '',
              quantity: formatFixed(li.quantity, 2),
              unit: li.unit,
              unit_price: formatFixed(li.unit_price, 2),
              tax_rate: formatFixed(li.tax_rate ?? DEFAULT_TAX_RATE, 2),
              discount_percent: formatFixed(li.discount_percent ?? 0, 2),
              _catalogMeta: catalogItem ? {
                sku: catalogItem.item_number,
                kind: catalogItem.kind,
                purchase_price: catalogItem.purchase_price ? parseFloat(catalogItem.purchase_price) : null,
              } : undefined,
              // FE-BILL-HF-R37-1: Preserve scope items count
              scope_items_count: li.scope_items_count ?? 0,
              // FE-BILL-HF-R38-4: Preserve payment milestone from backend
              payment_milestone_id: li.payment_milestone_id ?? null,
            };
          });

          // Add empty line for quick add
          const maxPosition = Math.max(...existingItems.map((li) => li.position));
          setLineItems([...existingItems, {
            position: maxPosition + 1,
            name: '',
            description: '',
            quantity: '1.00',
            unit: 'Stk',
            unit_price: '0.00',
            tax_rate: formatFixed(DEFAULT_TAX_RATE, 2),
            discount_percent: '0.00',
          }]);
        } else {
          // No existing items, add one empty line
          setLineItems([{
            position: 1,
            name: '',
            description: '',
            quantity: '1.00',
            unit: 'Stk',
            unit_price: '0.00',
            tax_rate: formatFixed(DEFAULT_TAX_RATE, 2),
            discount_percent: '0.00',
          }]);
        }

        if (offerData.project_id) {
          const projectData = await ProjectService.get(offerData.project_id);
          setProject(projectData);
        }
        if (offerData.business_partner_id) {
          const partnerData = await BusinessPartnerService.get(offerData.business_partner_id);
          setPartner(partnerData);
        }

        // Load payment plan if exists
        const paymentPlan = await OfferService.getPaymentPlan(parseInt(offerId, 10));
        if (paymentPlan && paymentPlan.milestones.length > 0) {
          setExistingPaymentPlan(paymentPlan);
          setPaymentPlanEnabled(true);
          setPaymentMilestones(paymentPlan.milestones);
          setPaymentPlanTemplate(paymentPlan.scheme_type || 'custom');

          // FE-BILL-HF-R38-4: Use payment_milestone_id directly from offer lines
          const milestonesInfo: MilestoneInfo[] = (paymentPlan.milestones || [])
            .filter((m): m is PaymentMilestone & { id: number } => m.id !== undefined)
            .map((m) => ({
              id: m.id,
              label: m.label,
              percentage: m.percentage,
              status: m.status || 'PLANNED',
              amount_net: m.amount_net,
            }));
          setMilestonesFromAssignments(milestonesInfo);
          setMilestoneAssignmentsByLineId(byLineIdFromLines);
          console.log('[DEBUG] Using byLineIdFromLines as primary source:', byLineIdFromLines);
        }

        // FE-BILL-R34-1: Load payment plan templates
        try {
          setLoadingTemplates(true);
          const templates = await OfferService.fetchPaymentPlanTemplates();
          setPaymentPlanTemplates(templates);
        } catch (templatesErr) {
          console.error('Failed to load payment plan templates:', templatesErr);
        } finally {
          setLoadingTemplates(false);
        }
      } else {
        if (projectIdParam) {
          const projectData = await ProjectService.get(parseInt(projectIdParam, 10));
          setProject(projectData);
        }
        if (partnerIdParam) {
          const partnerData = await BusinessPartnerService.get(parseInt(partnerIdParam, 10));
          setPartner(partnerData);
        }

        const defaultValidUntil = new Date();
        defaultValidUntil.setDate(defaultValidUntil.getDate() + 30);
        setValidUntil(defaultValidUntil.toISOString().split('T')[0]);

        // Add initial empty line for new offers
        setLineItems([{
          position: 1,
          name: '',
          description: '',
          quantity: '1.00',
          unit: 'Stk',
          unit_price: '0.00',
          tax_rate: formatFixed(DEFAULT_TAX_RATE, 2),
          discount_percent: '0.00',
        }]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [isEdit, offerId, projectIdParam, partnerIdParam]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Global dragend listener to reset drag state if onDragEnd doesn't fire (e.g., element removed during drag)
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      setDraggedLineIndex(null);
      setDragOverMilestoneId(null);
    };

    document.addEventListener('dragend', handleGlobalDragEnd);
    return () => document.removeEventListener('dragend', handleGlobalDragEnd);
  }, []);

  useEffect(() => {
    if (!partner) return;

    const searchProjects = async () => {
      setLoadingProjects(true);
      try {
        const results = await ProjectService.searchByPartner(partner.id, projectSearch || undefined);
        setProjectOptions(results);
      } catch (err) {
        console.error('Failed to search projects:', err);
      } finally {
        setLoadingProjects(false);
      }
    };

    const debounce = setTimeout(searchProjects, 300);
    return () => clearTimeout(debounce);
  }, [partner, projectSearch]);

  useEffect(() => {
    const searchPartners = async () => {
      setLoadingPartners(true);
      try {
        const results = await BusinessPartnerService.list({ kind: 'CUSTOMER', search: partnerSearch || undefined });
        setPartnerOptions(results);
      } catch (err) {
        console.error('Failed to search partners:', err);
      } finally {
        setLoadingPartners(false);
      }
    };

    const debounce = setTimeout(searchPartners, 300);
    return () => clearTimeout(debounce);
  }, [partnerSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setProjectDropdownOpen(false);
      }
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(event.target as Node)) {
        setPartnerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePartnerSelect = (selectedPartner: BusinessPartner) => {
    setPartner(selectedPartner);
    setProject(null);
    setPartnerDropdownOpen(false);
    setPartnerSearch('');
  };

  const handleClearPartner = () => {
    setPartner(null);
    setProject(null);
    setPartnerSearch('');
  };

  const handleProjectSelect = (selectedProject: Project) => {
    setProject(selectedProject);
    setProjectDropdownOpen(false);
    setProjectSearch('');
  };

  const handleClearProject = () => {
    setProject(null);
    setProjectSearch('');
  };

  const handleProjectCreated = async () => {
    setProjectModalOpen(false);
    if (partner) {
      const projects = await ProjectService.listByPartner(partner.id);
      setProjectOptions(projects);
      if (projects.length > 0) {
        const newest = projects.reduce((a, b) => (a.id > b.id ? a : b));
        setProject(newest);
      }
    }
  };

  const calculateLineTotal = (item: LineItemForm): { net: number; gross: number } => {
    const qty = parseLocaleNumber(item.quantity) || 0;
    const price = parseLocaleNumber(item.unit_price) || 0;
    const discount = parseLocaleNumber(item.discount_percent) || 0;
    const tax = parseLocaleNumber(item.tax_rate) || 0;

    const baseNet = qty * price;
    const discountAmount = baseNet * (discount / 100);
    const net = baseNet - discountAmount;
    const taxAmount = net * (tax / 100);
    const gross = net + taxAmount;
    return { net, gross };
  };

  const isLineEmpty = (item: LineItemForm): boolean => {
    return !item.name.trim() && !item.catalog_item_id;
  };

  // FE-BILL-HF-R35-3: One true ID helper to safely extract offer_line_id
  const getOfferLineId = (row: LineItemForm | any): number | null => {
    const id = row?.offer_line_id ?? row?.id ?? row?.offerLineId ?? row?.line_id ?? row?.pk ?? null;
    if (id === null || id === undefined) {
      if (process.env.NODE_ENV === 'development' && row?.name) {
        console.warn('[getOfferLineId] No ID found for row:', { name: row.name, row });
      }
      return null;
    }
    const n = Number(id);
    if (!Number.isFinite(n) || n <= 0) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[getOfferLineId] Invalid ID:', { id, n, row });
      }
      return null;
    }
    return n;
  };

  const calculateTotals = (): { subtotalNet: number; totalTax: number; totalGross: number } => {
    let subtotalNet = 0;
    let totalTax = 0;

    lineItems.filter((item) => !isLineEmpty(item)).forEach((item) => {
      const { net } = calculateLineTotal(item);
      const tax = parseLocaleNumber(item.tax_rate) || 0;
      subtotalNet += net;
      totalTax += net * (tax / 100);
    });

    return {
      subtotalNet,
      totalTax,
      totalGross: subtotalNet + totalTax,
    };
  };

  const createEmptyLine = (position: number): LineItemForm => ({
    position,
    name: '',
    description: '',
    quantity: '1.00',
    unit: 'Stk',
    unit_price: '0.00',
    tax_rate: formatFixed(DEFAULT_TAX_RATE, 2),
    discount_percent: '0.00',
  });

  const handleAddLine = () => {
    const newPosition = lineItems.length > 0 ? Math.max(...lineItems.map((li) => li.position)) + 1 : 1;
    setLineItems([...lineItems, createEmptyLine(newPosition)]);
  };

  // Count of filled lines - when this changes, we may need to add a new empty line
  const filledLinesCount = lineItems.filter((li) => !isLineEmpty(li)).length;

  // Ensure there's always one empty line for quick add after an item is selected
  useEffect(() => {
    if (filledLinesCount > 0) {
      const hasEmptyLine = lineItems.some(isLineEmpty);
      if (!hasEmptyLine) {
        const newPosition = Math.max(...lineItems.map((li) => li.position)) + 1;
        setLineItems((prev) => [...prev, createEmptyLine(newPosition)]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledLinesCount]);

  const handleRemoveLine = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleLineChange = (index: number, field: keyof LineItemForm, value: string | number) => {
    setLineItems(lineItems.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const getPurchasePrice = (item: LineItemForm): number | null => {
    if (item._catalogMeta?.purchase_price != null && item._catalogMeta.purchase_price > 0) {
      return item._catalogMeta.purchase_price;
    }
    const unitPrice = parseLocaleNumber(item.unit_price);
    if (unitPrice != null && unitPrice > 0) {
      return unitPrice * 0.7;
    }
    return null;
  };

  const hasCatalogPurchasePrice = (item: LineItemForm): boolean => {
    return item._catalogMeta?.purchase_price != null && item._catalogMeta.purchase_price > 0;
  };

  const handleMarkupChange = (index: number, markupValue: string) => {
    setLineItems(lineItems.map((item, i) => {
      if (i === index) {
        const purchasePrice = getPurchasePrice(item);
        const markup = parseLocaleNumber(markupValue);
        if (purchasePrice != null && purchasePrice > 0 && markup !== null && markup > 0) {
          const newUnitPrice = purchasePrice * markup;
          return { ...item, unit_price: formatFixed(newUnitPrice, 2) };
        }
      }
      return item;
    }));
  };

  const calculateMarkup = (item: LineItemForm): string => {
    const purchasePrice = getPurchasePrice(item);
    const unitPrice = parseLocaleNumber(item.unit_price);
    if (purchasePrice != null && purchasePrice > 0 && unitPrice != null && unitPrice > 0) {
      return formatFixed(unitPrice / purchasePrice, 2);
    }
    return '';
  };

  // Payment Plan handlers
  const handlePaymentPlanTemplateChange = (template: string) => {
    setPaymentPlanTemplate(template);

    if (template === 'none') {
      setPaymentMilestones([]);
      return;
    }

    if (template === '50-50') {
      setPaymentMilestones([
        {
          label: 'Down payment',
          sequence: 1,
          milestone_type: 'DOWN_PAYMENT',
          percentage: 50,
          trigger_type: 'ON_OFFER_ACCEPTED',
          due_offset_days: 7,
        },
        {
          label: 'Final payment',
          sequence: 2,
          milestone_type: 'FINAL',
          percentage: 50,
          trigger_type: 'ON_PROJECT_STATUS',
          trigger_project_status: 'COMPLETED',
          due_offset_days: 14,
        },
      ]);
    } else if (template === '50-25-25') {
      setPaymentMilestones([
        {
          label: 'Down payment',
          sequence: 1,
          milestone_type: 'DOWN_PAYMENT',
          percentage: 50,
          trigger_type: 'ON_OFFER_ACCEPTED',
          due_offset_days: 7,
        },
        {
          label: 'Progress payment',
          sequence: 2,
          milestone_type: 'PROGRESS',
          percentage: 25,
          trigger_type: 'ON_PROJECT_STATUS',
          trigger_project_status: 'IN_PROGRESS',
          due_offset_days: 14,
        },
        {
          label: 'Final payment',
          sequence: 3,
          milestone_type: 'FINAL',
          percentage: 25,
          trigger_type: 'ON_PROJECT_STATUS',
          trigger_project_status: 'COMPLETED',
          due_offset_days: 14,
        },
      ]);
    } else if (template === 'custom') {
      // Keep existing milestones or start with empty
      if (paymentMilestones.length === 0) {
        setPaymentMilestones([
          {
            label: 'Payment 1',
            sequence: 1,
            milestone_type: 'DOWN_PAYMENT',
            percentage: 100,
            trigger_type: 'ON_OFFER_ACCEPTED',
            due_offset_days: 14,
          },
        ]);
      }
    }
  };

  const handleAddMilestone = () => {
    const newSeq = paymentMilestones.length + 1;
    setPaymentMilestones([
      ...paymentMilestones,
      {
        label: `Payment ${newSeq}`,
        sequence: newSeq,
        milestone_type: 'PROGRESS',
        percentage: 0,
        trigger_type: 'MANUAL',
        due_offset_days: 14,
      },
    ]);
  };

  // FE-BILL-R34-1: Apply Payment Plan Template from API
  const handleApplyPaymentPlanTemplate = async (templateId: number) => {
    // FE-BILL-HF-R34-6: Guard against invalid templateId
    if (!templateId || !Number.isFinite(templateId)) {
      return;
    }

    if (!offerId || !isEdit) {
      toast.error('Bitte speichern Sie das Angebot zuerst');
      return;
    }

    const confirmed = window.confirm('Vorhandene Milestones werden ersetzt. Fortfahren?');
    if (!confirmed) return;

    try {
      await OfferService.applyPaymentPlanTemplate(parseInt(offerId, 10), templateId);
      toast.success('Milestone-Template angewendet');

      // FE-BILL-HF-R34-3: Clear preview after apply
      setPreviewTemplate(null);
      setSelectedTemplateId(null);

      // FE-BILL-R34-2: Refresh payment plan and milestone assignments with real labels
      const updatedPlan = await OfferService.getPaymentPlan(parseInt(offerId, 10));
      if (updatedPlan && updatedPlan.milestones.length > 0) {
        setExistingPaymentPlan(updatedPlan);
        setPaymentMilestones(updatedPlan.milestones);
        setPaymentPlanTemplate(updatedPlan.scheme_type || 'custom');
        setPaymentPlanEnabled(true);
      }

      // Refresh milestone assignments for line items sections
      const assignmentsData = await OfferService.getMilestoneAssignments(parseInt(offerId, 10));
      setMilestonesFromAssignments(assignmentsData.milestones || []);
      const byLineId: Record<number, number | null> = {};
      (assignmentsData.assignments || []).forEach((assignment) => {
        byLineId[assignment.position_id] = assignment.payment_milestone_id;
      });
      (assignmentsData.unassigned_positions || []).forEach((pos) => {
        byLineId[pos.id] = null;
      });
      setMilestoneAssignmentsByLineId(byLineId);
    } catch (err) {
      console.error('Failed to apply payment plan template:', err);
      toast.error('Template konnte nicht angewendet werden');
    }
  };

  // FE-BILL-HF-R34-3: Load template preview when selecting from dropdown
  const handleTemplatePreviewSelect = async (templateId: number) => {
    // FE-BILL-HF-R34-6: Guard against invalid templateId
    if (!templateId || !Number.isFinite(templateId)) {
      setSelectedTemplateId(null);
      setPreviewTemplate(null);
      return;
    }

    setSelectedTemplateId(templateId);
    setLoadingPreview(true);
    setPreviewTemplate(null);

    try {
      const templateDetail = await OfferService.fetchPaymentPlanTemplate(templateId);
      setPreviewTemplate(templateDetail);
    } catch (err) {
      console.error('Failed to load template preview:', err);
      toast.error('Vorschau konnte nicht geladen werden');
    } finally {
      setLoadingPreview(false);
    }
  };

  // FE-BILL-HF-R34-3: Format milestone preview text
  const formatMilestonePreview = (milestones: { label: string; percentage: number }[]): string => {
    const maxShow = 4;
    const displayed = milestones.slice(0, maxShow);
    const remaining = milestones.length - maxShow;

    const previewText = displayed.map((m) => `${m.label} (${m.percentage}%)`).join(' · ');

    if (remaining > 0) {
      return `${previewText} · +${remaining} mehr`;
    }
    return previewText;
  };

  // FE-BILL-HF-R38-4: Refresh milestone assignments from offer detail (not the old assignments API)
  const refreshMilestoneAssignments = async () => {
    if (!offerId || !isEdit) return;

    try {
      // Reload offer to get updated payment_milestone_id on lines
      const offerData = await OfferService.get(parseInt(offerId, 10));
      const byLineId: Record<number, number | null> = {};
      offerData.line_items?.forEach((li) => {
        if (li.id) {
          byLineId[li.id] = li.payment_milestone_id ?? null;
        }
      });
      console.log('[DEBUG] refreshMilestoneAssignments - byLineId:', byLineId);
      setMilestoneAssignmentsByLineId(byLineId);
    } catch (err) {
      console.error('Failed to refresh milestone assignments:', err);
    }
  };

  // FE-BILL-R34-1 + FE-BILL-HF-R35-2: Handle milestone assignment change (inline dropdown / DnD)
  const handleMilestoneAssignmentChange = async (lineId: number | undefined, milestoneId: number | null) => {
    if (!offerId || !isEdit) return;

    // FE-BILL-HF-R35-2: Guard against undefined/NaN offer_line_id
    if (!Number.isFinite(lineId)) {
      toast.error('Zuordnung fehlgeschlagen: Ungültige Position-ID');
      console.error('[milestone-assign] Invalid lineId:', lineId);
      return;
    }

    // FE-BILL-HF-R35-2: Guard against NaN milestone_id (null is valid for unassign)
    if (milestoneId !== null && !Number.isFinite(milestoneId)) {
      toast.error('Zuordnung fehlgeschlagen: Ungültige Milestone-ID');
      console.error('[milestone-assign] Invalid milestoneId:', milestoneId);
      return;
    }

    const previousMilestoneId = milestoneAssignmentsByLineId[lineId!];
    if (previousMilestoneId === milestoneId) return;

    // Optimistic update
    setMilestoneAssignmentsByLineId((prev) => ({ ...prev, [lineId!]: milestoneId }));
    setSavingLineId(lineId!);

    const payload = { offer_line_id: lineId, payment_milestone_id: milestoneId };

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[milestone-assign] POST payload:', payload);
      }
      await OfferService.assignOfferLineToMilestone(parseInt(offerId, 10), lineId!, milestoneId);
      toast.success('Zuordnung gespeichert');
      await refreshMilestoneAssignments();
    } catch (err: any) {
      // FE-BILL-HF-R35-2: Show BE error detail in toast
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Unbekannter Fehler';
      const code = err?.response?.data?.code;
      const status = err?.response?.status;

      console.error('[milestone-assign] Failed:', { payload, status, response: err?.response?.data, error: err });

      const errorMsg = code
        ? `Zuordnung fehlgeschlagen: ${detail} (code=${code})`
        : `Zuordnung fehlgeschlagen: ${detail}`;
      toast.error(errorMsg);

      // Rollback
      setMilestoneAssignmentsByLineId((prev) => ({ ...prev, [lineId!]: previousMilestoneId }));
    } finally {
      setSavingLineId(null);
    }
  };

  // Handle milestone assignment for NEW lines (not yet saved, no ID)
  const handlePendingMilestoneChange = (index: number, milestoneId: number | null) => {
    setPendingMilestoneByIndex((prev) => ({ ...prev, [index]: milestoneId }));
  };

  // Get milestone value for a line (either from saved assignments or pending)
  const getMilestoneForLine = (index: number, lineId: number | null): number | null => {
    if (lineId !== null && milestoneAssignmentsByLineId[lineId] !== undefined) {
      return milestoneAssignmentsByLineId[lineId];
    }
    return pendingMilestoneByIndex[index] ?? null;
  };

  // FE-BILL-R34-1 + FE-BILL-HF-R35-2 + FE-BILL-HF-R35-3: Drag & Drop handlers for Line Items
  const handleLineDragStart = (e: React.DragEvent, index: number, item: LineItemForm) => {
    const offerLineId = getOfferLineId(item);

    // Guard against undefined/invalid lineId
    if (offerLineId === null) {
      console.warn('[DnD] Cannot drag item without valid offerLineId:', { index, item });
      e.preventDefault();
      return;
    }

    // Set dataTransfer FIRST, before any state changes
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-offer-line-id', String(offerLineId));
    e.dataTransfer.setData('text/plain', String(offerLineId));

    // IMPORTANT: Defer state update to next frame so Chrome can "commit" the drag
    requestAnimationFrame(() => {
      setDraggedLineIndex(index);
    });
  };

  const handleLineDragEnd = () => {
    setDraggedLineIndex(null);
    setDragOverMilestoneId(null);
  };

  const handleMilestoneDragOver = (e: React.DragEvent, milestoneId: number | 'unassigned') => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverMilestoneId(milestoneId);

    // Auto-scroll when dragging near viewport edges
    const scrollThreshold = 80;
    const scrollSpeed = 15;
    const mouseY = e.clientY;
    const viewportHeight = window.innerHeight;

    if (mouseY < scrollThreshold) {
      window.scrollBy({ top: -scrollSpeed, behavior: 'auto' });
    } else if (mouseY > viewportHeight - scrollThreshold) {
      window.scrollBy({ top: scrollSpeed, behavior: 'auto' });
    }
  };

  const handleMilestoneDragLeave = () => {
    setDragOverMilestoneId(null);
  };

  const handleMilestoneDrop = async (e: React.DragEvent, targetMilestoneId: number | null) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset drag state IMMEDIATELY to prevent blocking subsequent drags
    setDragOverMilestoneId(null);
    setDraggedLineIndex(null);

    const lineIdStr =
      e.dataTransfer.getData('application/x-offer-line-id') ||
      e.dataTransfer.getData('text/plain') ||
      e.dataTransfer.getData('text');

    if (!lineIdStr) {
      toast.error('Drag-Daten ungültig');
      return;
    }

    const offerLineId = Number(lineIdStr);
    if (!Number.isFinite(offerLineId) || offerLineId <= 0) {
      toast.error('Ungültige Position-ID');
      return;
    }

    await handleMilestoneAssignmentChange(offerLineId, targetMilestoneId);
  };

  // FE-BILL-R34-1: Helper functions for milestone sections
  const getFilledLinesWithMilestoneInfo = (): Array<{ line: LineItemForm; index: number; milestoneId: number | null }> => {
    return lineItems
      .map((line, index) => ({
        line,
        index,
        milestoneId: line.id ? (milestoneAssignmentsByLineId[line.id] ?? null) : null,
      }))
      .filter(({ line }) => !isLineEmpty(line));
  };

  const getLinesForMilestone = (milestoneId: number): Array<{ line: LineItemForm; index: number }> => {
    return getFilledLinesWithMilestoneInfo()
      .filter(({ milestoneId: mid }) => mid === milestoneId)
      .map(({ line, index }) => ({ line, index }));
  };

  const getUnassignedLines = (): Array<{ line: LineItemForm; index: number }> => {
    return getFilledLinesWithMilestoneInfo()
      .filter(({ milestoneId }) => milestoneId === null)
      .map(({ line, index }) => ({ line, index }));
  };

  const calculateMilestoneSectionTotal = (milestoneId: number): number => {
    return getLinesForMilestone(milestoneId).reduce((sum, { line }) => sum + calculateLineTotal(line).net, 0);
  };

  const getMilestoneStatusColor = (status: string): { bg: string; text: string } => {
    switch (status?.toUpperCase()) {
      case 'PLANNED':
        return { bg: '#f3f4f6', text: '#374151' };
      case 'READY_FOR_INVOICE':
      case 'READY':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'INVOICED':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'PAID':
        return { bg: '#d1fae5', text: '#065f46' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  // Check if we should show milestone sections in line items table
  const shouldShowMilestoneSections = isEdit && paymentPlanEnabled && milestonesFromAssignments.length > 0;

  const handleRemoveMilestone = (index: number) => {
    setPaymentMilestones(
      paymentMilestones
        .filter((_, i) => i !== index)
        .map((m, i) => ({ ...m, sequence: i + 1 }))
    );
  };

  const handleMilestoneChange = (index: number, field: keyof PaymentMilestone, value: any) => {
    setPaymentMilestones(
      paymentMilestones.map((m, i) =>
        i === index ? { ...m, [field]: value } : m
      )
    );
  };

  const getMilestonePercentageSum = (): number => {
    return paymentMilestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
  };

  const isPaymentPlanValid = (): boolean => {
    const sum = getMilestonePercentageSum();
    return Math.abs(sum - 100) <= 1; // 1% tolerance
  };

  const handleCatalogSelect = (index: number, catalogItem: CatalogItem) => {
    setLineItems(lineItems.map((item, i) => {
      if (i === index) {
        const salesPrice = parseFloat(catalogItem.sales_price) || 0;
        const taxRate = parseFloat(catalogItem.tax_rate || '') || DEFAULT_TAX_RATE;
        const purchasePrice = catalogItem.purchase_price ? parseFloat(catalogItem.purchase_price) : null;
        return {
          ...item,
          catalog_item_id: catalogItem.id,
          name: catalogItem.name,
          description: catalogItem.description || '',
          unit: catalogItem.unit,
          unit_price: formatFixed(salesPrice, 2),
          tax_rate: formatFixed(taxRate, 2),
          _catalogMeta: {
            sku: catalogItem.item_number,
            kind: catalogItem.kind,
            purchase_price: purchasePrice,
          },
        };
      }
      return item;
    }));
  };

  const handleSave = async (openPreview: boolean = false) => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (!partner) {
      toast.error('No customer selected');
      return;
    }

    // Filter out empty lines before validation
    const filledLineItems = lineItems.filter((li) => !isLineEmpty(li));

    if (filledLineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    // Validate only filled lines
    const invalidLines = filledLineItems.filter((li) => {
      const qty = parseLocaleNumber(li.quantity) || 0;
      return qty <= 0;
    });
    if (invalidLines.length > 0) {
      const issues = invalidLines.map((li) => `Line "${li.name}": invalid quantity`);
      toast.error(`Please fix: ${issues.join('; ')}`);
      return;
    }

    setSaving(true);
    try {
      const lineItemsPayload = filledLineItems.map((li, idx) => ({
        id: li.id, // Include ID for existing lines to preserve milestone assignments
        position: idx + 1,
        catalog_item_id: li.catalog_item_id,
        name: li.name,
        description: li.description || undefined,
        quantity: parseLocaleNumber(li.quantity) || 0,
        unit: li.unit,
        unit_price: parseLocaleNumber(li.unit_price) || 0,
        discount_percent: parseLocaleNumber(li.discount_percent) || undefined,
        tax_rate: parseLocaleNumber(li.tax_rate) || DEFAULT_TAX_RATE,
        // Include milestone assignment so it persists on save
        payment_milestone_id: li.id ? (milestoneAssignmentsByLineId[li.id] ?? null) : null,
      }));

      let savedOffer: Offer;

      const parsedPaymentTermsDays = paymentTermsDays.trim() !== '' ? parseInt(paymentTermsDays, 10) : null;

      if (isEdit && offerId) {
        const updatePayload: UpdateOfferPayload = {
          title,
          valid_until: validUntil || undefined,
          terms: terms || undefined,
          notes: notes || undefined,
          payment_terms_days: parsedPaymentTermsDays,
          line_items: lineItemsPayload,
        };
        savedOffer = await OfferService.update(parseInt(offerId, 10), updatePayload);
      } else {
        const createPayload: CreateOfferPayload = {
          project_id: project?.id,
          business_partner_id: partner.id,
          title,
          valid_until: validUntil || undefined,
          terms: terms || undefined,
          notes: notes || undefined,
          payment_terms_days: parsedPaymentTermsDays,
          line_items: lineItemsPayload,
        };
        savedOffer = await OfferService.create(createPayload);
        toast.success('Offer created');
      }

      // Handle Payment Plan
      if (paymentPlanEnabled && paymentMilestones.length > 0) {
        const plan: PaymentPlan = {
          offer: savedOffer.id,
          currency: 'EUR',
          total_net: savedOffer.subtotal_net,
          total_tax: savedOffer.total_tax,
          total_gross: savedOffer.total_gross,
          scheme_type: paymentPlanTemplate,
          milestones: paymentMilestones,
        };
        await OfferService.savePaymentPlan(savedOffer.id, plan);
      } else if (!paymentPlanEnabled && existingPaymentPlan) {
        // Delete existing payment plan if disabled
        await OfferService.deletePaymentPlan(savedOffer.id);
      }

      // Re-apply milestone assignments after save (backend may have assigned new IDs)
      if (savedOffer.line_items && savedOffer.line_items.length > 0) {
        const assignmentsByKey: Record<string, number | null> = {};

        const filledIndices = lineItems
          .map((li, idx) => ({ li, idx }))
          .filter(({ li }) => !isLineEmpty(li))
          .map(({ idx }) => idx);

        filledLineItems.forEach((li, payloadIdx) => {
          const originalIndex = filledIndices[payloadIdx];

          if (li.id && milestoneAssignmentsByLineId[li.id] !== undefined) {
            assignmentsByKey[`pos-${payloadIdx + 1}`] = milestoneAssignmentsByLineId[li.id];
          }
          else if (pendingMilestoneByIndex[originalIndex] !== undefined) {
            assignmentsByKey[`pos-${payloadIdx + 1}`] = pendingMilestoneByIndex[originalIndex];
          }
        });

        for (const serverLine of savedOffer.line_items) {
          const key = `pos-${serverLine.position}`;
          const milestoneId = assignmentsByKey[key];
          if (milestoneId !== undefined && milestoneId !== null && serverLine.id) {
            try {
              await OfferService.assignOfferLineToMilestone(savedOffer.id, serverLine.id, milestoneId);
            } catch (err) {
              console.warn(`Failed to assign line ${serverLine.id} to milestone ${milestoneId}:`, err);
            }
          }
        }

        // Clear pending assignments after successful save
        setPendingMilestoneByIndex({});
      }

      if (openPreview) {
        navigate(`/offers/${savedOffer.id}`);
      } else if (isEdit) {
        // Stay on edit page and reload data to get new line IDs
        await loadData();
        toast.success('Änderungen gespeichert - Positionen können jetzt Milestones zugeordnet werden');
      } else {
        // New offer created - navigate to edit page
        navigate(`/offers/${savedOffer.id}/edit`);
      }
    } catch (err: any) {
      console.error('Failed to save offer:', err);
      const message = err?.response?.data?.detail || 'Save failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (offerId) {
      navigate(`/offers/${offerId}`);
    } else {
      navigate('/sales');
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
      <OfferHeaderCard
        isEdit={isEdit}
        offer={offer}
        partner={partner}
        project={project}
        saving={saving}
        onSaveDraft={() => handleSave(false)}
        onSaveAndPreview={() => handleSave(true)}
        onBack={handleBack}
      />

      <OfferFormSection
        isEdit={isEdit}
        partner={partner}
        project={project}
        partnerSearch={partnerSearch}
        onPartnerSearchChange={setPartnerSearch}
        partnerOptions={partnerOptions}
        partnerDropdownOpen={partnerDropdownOpen}
        onPartnerDropdownToggle={() => setPartnerDropdownOpen(!partnerDropdownOpen)}
        loadingPartners={loadingPartners}
        onPartnerSelect={handlePartnerSelect}
        onClearPartner={handleClearPartner}
        partnerDropdownRef={partnerDropdownRef}
        projectSearch={projectSearch}
        onProjectSearchChange={setProjectSearch}
        projectOptions={projectOptions}
        projectDropdownOpen={projectDropdownOpen}
        onProjectDropdownToggle={() => setProjectDropdownOpen(!projectDropdownOpen)}
        loadingProjects={loadingProjects}
        onProjectSelect={handleProjectSelect}
        onClearProject={handleClearProject}
        projectDropdownRef={projectDropdownRef}
        onOpenProjectModal={() => {
          setProjectDropdownOpen(false);
          setProjectModalOpen(true);
        }}
        title={title}
        onTitleChange={setTitle}
        validUntil={validUntil}
        onValidUntilChange={setValidUntil}
        paymentTermsDays={paymentTermsDays}
        onPaymentTermsDaysChange={(newDays) => {
          setPaymentTermsDays(newDays);
          const daysNum = parseInt(newDays, 10);
          if (!isNaN(daysNum) && daysNum > 0) {
            setTerms(generatePaymentTermsText(daysNum));
          } else if (newDays === '') {
            setTerms(generatePaymentTermsText(DEFAULT_PAYMENT_DAYS));
          }
        }}
        terms={terms}
        onTermsChange={setTerms}
      />

      <OfferLineItemsTable
        lineItems={lineItems}
        shouldShowMilestoneSections={shouldShowMilestoneSections}
        milestonesFromAssignments={milestonesFromAssignments}
        draggedLineIndex={draggedLineIndex}
        dragOverMilestoneId={dragOverMilestoneId}
        savingLineId={savingLineId}
        totals={totals}
        onAddLine={handleAddLine}
        onRemoveLine={handleRemoveLine}
        onLineChange={handleLineChange}
        onCatalogSelect={handleCatalogSelect}
        onMarkupChange={handleMarkupChange}
        onScopeDrawerOpen={setScopeDrawerLine}
        onLineDragStart={handleLineDragStart}
        onLineDragEnd={handleLineDragEnd}
        onMilestoneDragOver={handleMilestoneDragOver}
        onMilestoneDragLeave={handleMilestoneDragLeave}
        onMilestoneDrop={handleMilestoneDrop}
        onMilestoneAssignmentChange={handleMilestoneAssignmentChange}
        onPendingMilestoneChange={handlePendingMilestoneChange}
        isLineEmpty={isLineEmpty}
        calculateLineTotal={calculateLineTotal}
        calculateMarkup={calculateMarkup}
        getPurchasePrice={getPurchasePrice}
        hasCatalogPurchasePrice={hasCatalogPurchasePrice}
        getOfferLineId={getOfferLineId}
        getMilestoneForLine={getMilestoneForLine}
        getLinesForMilestone={getLinesForMilestone}
        getUnassignedLines={getUnassignedLines}
        calculateMilestoneSectionTotal={calculateMilestoneSectionTotal}
        getMilestoneStatusColor={getMilestoneStatusColor}
        formatCurrency={formatCurrency}
      />

      <PaymentPlanSection
        isEdit={isEdit}
        paymentPlanEnabled={paymentPlanEnabled}
        onPaymentPlanEnabledChange={(checked) => {
          setPaymentPlanEnabled(checked);
          if (checked && paymentMilestones.length === 0) {
            handlePaymentPlanTemplateChange('50-50');
          }
        }}
        paymentPlanTemplate={paymentPlanTemplate}
        onPaymentPlanTemplateChange={handlePaymentPlanTemplateChange}
        paymentMilestones={paymentMilestones}
        existingPaymentPlan={existingPaymentPlan}
        paymentPlanTemplates={paymentPlanTemplates}
        loadingTemplates={loadingTemplates}
        selectedTemplateId={selectedTemplateId}
        onTemplatePreviewSelect={handleTemplatePreviewSelect}
        onSetSelectedTemplateId={setSelectedTemplateId}
        onApplyTemplate={handleApplyPaymentPlanTemplate}
        previewTemplate={previewTemplate}
        onSetPreviewTemplate={setPreviewTemplate}
        loadingPreview={loadingPreview}
        onAddMilestone={handleAddMilestone}
        onRemoveMilestone={handleRemoveMilestone}
        onMilestoneChange={handleMilestoneChange}
        getMilestonePercentageSum={getMilestonePercentageSum}
        isPaymentPlanValid={isPaymentPlanValid}
        formatMilestonePreview={formatMilestonePreview}
        totalGross={totals.totalGross}
        minAmount={PAYMENT_PLAN_MIN_AMOUNT}
        formatCurrency={formatCurrency}
      />

      {/* FE-BILL-R34-1: Milestone assignment UI is now integrated in Line Items table above */}

      <RecurringContractSection
        config={recurringConfig}
        onChange={setRecurringConfig}
      />

      {/* Notes */}
      <div
        style={{
          background: 'white',
          border: '1px solid #e0e3ef',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        <label style={labelStyle}>Internal notes (not shown on offer)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Internal notes..."
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      {/* Project Modal */}
      {partner && (
        <ProjectModal
          isOpen={projectModalOpen}
          project={null}
          partnerId={partner.id}
          onClose={() => setProjectModalOpen(false)}
          onSave={handleProjectCreated}
        />
      )}

      {/* Line Item Scope Drawer - for viewing/editing subtasks */}
      {scopeDrawerLine && (
        <OfferLineScopeDrawer
          offerLineId={scopeDrawerLine.id}
          lineName={scopeDrawerLine.name}
          isOpen={true}
          onClose={() => setScopeDrawerLine(null)}
        />
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};
